import { create } from 'zustand'
import {
  getActiveCompany,
  logger,
  isReady as appContextReady,
  CompanyPreferences,
  getUserDetails,
  exceptionMessageHandler,
  appConfig,
} from '$utils'
import { mercuryApi } from '$api'
import dayjs, { Dayjs } from 'dayjs'
import { RangeFilter } from '$constants'
import { ActiveCompany, ReportsMetadata, UserDetails } from '$models'
import { subscribeWithSelector } from 'zustand/middleware'

function initializeAppStateStore(resetReportsMetadata = true) {
  // We immediately fetch feature flags the first time this module is used. We
  // can rely upon the fact only the first import will actually execute this
  // to avoid unnecessary calls.
  logger.debug('Initialising appState store')

  appContextReady().then(() => {
    fetchAndUpdateFeatureFlags()
    const activeCompany = getActiveCompany()
    logger.debug('Active company', activeCompany)

    const today = dayjs()
    const activeCompanyDayStartPreference: string =
      activeCompany?.preference(CompanyPreferences.DayStart)?.split(':') ||
      '05:00:00'
    const todayAtCompanyStartTime = today
      .set('hour', +activeCompanyDayStartPreference[0])
      .set('minute', +activeCompanyDayStartPreference[1])
      .set('second', +activeCompanyDayStartPreference[2])
      .set('millisecond', 0)

    const userDetails = getUserDetails()
    logger.debug('Active user', userDetails)

    useAppStore.setState({
      isAppStateReady: true,
      activeCompany: activeCompany,
      userDetails: userDetails,
      ...(resetReportsMetadata && {
        reportsMetadata: {
          // We default to using a single day of data, starting at the beginning
          // of the current day, and ending at the beginning of the next day.
          startDateTime: todayAtCompanyStartTime,
          endDateTime: todayAtCompanyStartTime.add(1, 'day'),
          dateRange: RangeFilter.Day,
          prevDateRange: RangeFilter.Day,
        },
      }),
    })
  })
}

initializeAppStateStore()

type AppState = {
  featureFlags: Set<string>
  reportsMetadata: ReportsMetadata
  isAppStateReady: boolean
  activeCompany: ActiveCompany | null
  userDetails: UserDetails
}

type AppActions = {
  setFeatureFlags: (newFlags: string[]) => void
  setReportsMetadata: (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    dateRange: RangeFilter,
    prevDateRange: RangeFilter
  ) => void
  reset: () => void
}

const initialState: AppState = {
  reportsMetadata: {
    startDateTime: dayjs(),
    endDateTime: dayjs(),
    dateRange: RangeFilter.Day,
    prevDateRange: RangeFilter.Day,
  },
  featureFlags: new Set<string>(),

  isAppStateReady: false,
  activeCompany: null,
  userDetails: {},
}

/**
 * Overall app state store
 */
const useAppStore = create(
  subscribeWithSelector<AppState & AppActions>((set) => ({
    ...initialState,

    // actions
    setFeatureFlags: (newFlags: string[]) =>
      set(() => ({ featureFlags: new Set(newFlags) })),

    setReportsMetadata: (
      start: Dayjs,
      end: Dayjs,
      range: RangeFilter,
      prevRange: RangeFilter
    ) =>
      set({
        reportsMetadata: {
          startDateTime: start,
          endDateTime: end,
          dateRange: range,
          prevDateRange: prevRange,
        },
      }),

    reset: () => set(initialState),
  }))
)

export default useAppStore

// Listen for notification of context changes and update any store data that is
// context dependent.
window.addEventListener('@@loaded/appContextChange', (ev: Event) => {
  const { type } = (ev as CustomEvent).detail
  logger.debug(
    'AppState store received notification the app context %s data has changed',
    type
  )

  if (type === 'user-info') {
    // no store data is dependent on user info yet
  }

  if (type === 'company') {
    /* We need to initialize the app state with the new active company
    but we want to keep the reports metadata between companies */
    initializeAppStateStore(false)
  }
})

// Helper function to actually get feature flags from the API and update the
// store. When components read from the store they should get the most up to
// date flags.
function fetchAndUpdateFeatureFlags() {
  mercuryApi
    .get<{ [key: string]: boolean }>('/features')
    .then((featureFlagData) => {
      // Build an array of the names of each feature flag that is set to `true`
      // in the API response.
      const flags = Object.keys(featureFlagData).filter(
        (flag) => featureFlagData[flag]
      )

      logger.debug('Active feature flags are %o', flags)

      // and update the store, this will trigger a re-render of any components
      // that read feature flags from the store.
      useAppStore.getState().setFeatureFlags(flags)
    })
    .catch((error) => {
      logger.error('Unable to read feature flags %s due to %o', error)
      exceptionMessageHandler(error, 'Unable to read feature flags')
    })
}

/* The purpose of this function is to listen to the changes on the 
AppState store isAppStateReady property so the page loaders can await on the
AppState to update it and be able to read the active company and reports metadata */
export async function isAppStateReady() {
  return new Promise<void>((resolve) => {
    //This standalone value is set in react-app/index.html, which is the entry point
    //for standalone pages
    const isStandalone = appConfig.get('standalone') ?? false

    if (isStandalone) {
      logger.warn(
        'Running in standalone mode, not waiting for AngularJS app state'
      )
      resolve()
    }

    if (useAppStore.getState().isAppStateReady) {
      logger.debug('AppState is already ready')
      resolve()
    }

    const unsubscribe = useAppStore.subscribe(
      (state) => state.isAppStateReady,
      (isNowReady) => {
        logger.debug(
          'appStateAwaiter promise heard state change to',
          isNowReady
        )
        if (isNowReady) {
          unsubscribe()
          resolve()
        }
      }
    )
  })
}
